import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useNuxtApp } from "#app";
import { collection, getDocs } from "firebase/firestore";
import {
  isExclusiveVariant,
  exclusiveBadgeText as exclusiveTextFromVariant,
  readVariantQty,
} from "./variantDisplay";

/* ---- Pure helpers ---- */

export function getColorValue(c) {
  return typeof c === "string" ? c : c?.value || "";
}

function normSkuPart(v) {
  return String(v || "")
    .toUpperCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/(^-|-$)/g, "");
}

function maskSkuPairs(s, limit = 4) {
  if (!s) return "";
  const cleaned = String(s).toUpperCase();
  const parts = cleaned.split(/[-\s_]+/).filter(Boolean);
  const pairs = parts.filter((p) => /^[A-Z]{2}$/.test(p));
  const chosen = (pairs.length ? pairs : parts).slice(0, limit);
  return chosen.join("-");
}

/* ====================================================================
 *  useProductVariants
 *  – завантажує варіанти, керує вибором color / size / lot
 *  – повертає derived-стан: ціну, наявність, SKU, ексклюзив-бейджі тощо
 * ==================================================================== */
export function useProductVariants(product) {
  const route = useRoute();
  const { $db } = useNuxtApp();

  const selectedColor = ref("");
  const selectedSize = ref("");
  const selectedVariantId = ref("");
  const variants = ref([]);

  /* ====================================================================
   *  ЗВІДКИ ВАРІАНТИ (п.9г)
   *
   *  Синхронно — з `product.variantsLite`. Це денормалізація, яку пише
   *  тригер `aggregateProductFromVariants` на кожен запис варіанта, і
   *  вона лежить у самому документі товару, тобто вже є в SSR-payload:
   *  ЖОДНОГО додаткового читання. Доти сторінка товару читала тільки
   *  сабколекцію — клієнтським SDK, тобто на сервері варіантів не було
   *  зовсім, і JSON-LD віддавав `OutOfStock` для наявних товарів, а
   *  ?vid із картки каталогу відпрацьовував лише після гідрації.
   *
   *  Асинхронно, вже в клієнті — повні документи сабколекції. Вони
   *  потрібні галереї: у lite є лише одна картинка на варіант, а
   *  `imagePaths` (кадри 2..N) живуть у документі. Заразом це фолбек
   *  для товарів, яких lite не покриває: у нього влазить 60 варіантів,
   *  і його немає в товарів, створених до тригера.
   *
   *  Порядок саме такий, бо гідрація має збігтися: перший рендер у
   *  клієнті мусить бачити ті самі дані, що й сервер, — тобто lite.
   * ==================================================================== */

  /** Список із денормалізації; lite тримає лише опубліковані. */
  function liteVariants(p) {
    const list = Array.isArray(p?.variantsLite) ? p.variantsLite : [];
    return list.filter((v) => v && v.published !== false);
  }

  /** Вибір за замовчуванням — тільки якщо його ще не зробив ?query. */
  function applyDefaults(p) {
    if (!selectedColor.value && (p.colors || []).length)
      selectedColor.value = getColorValue(p.colors[0]);
    if (!selectedSize.value && (p.sizes || []).length)
      selectedSize.value = p.sizes[0];
    if (
      !selectedVariantId.value &&
      Array.isArray(variants.value) &&
      variants.value.length
    )
      selectedVariantId.value = String(variants.value[0]?.id || "");
  }

  watch(
    product,
    (p) => {
      variants.value = liteVariants(p);
      if (!p?.id) return;
      applyQueryOptions();
      applyDefaults(p);
    },
    { immediate: true },
  );

  watch(
    product,
    async (p) => {
      if (!import.meta.client || !p?.id) return;
      try {
        const snap = await getDocs(
          collection($db, "products", p.id, "variants"),
        );
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((v) => v.published === true);
        // Порожній результат НЕ затирає lite: це або збій читання, або
        // товар без опублікованих варіантів — у обох випадках краще
        // лишити те, що вже показано.
        if (list.length) variants.value = list;
      } catch {
        /* лишаємось на lite */
      }

      // Ще раз: ?vid міг вказувати на варіант, якого немає в lite
      // (товар із понад 60 варіантами).
      applyQueryOptions();
      applyDefaults(p);
    },
    { immediate: true },
  );

  /* ---- URL query sync ---- */
  function applyQueryOptions() {
    const qc = String(route.query.color || "");
    const qs = String(route.query.size || "");
    const qv = String(route.query.vid || "");
    if (qc && (product.value.colors || []).some((c) => getColorValue(c) === qc))
      selectedColor.value = qc;
    if (qs && (product.value.sizes || []).includes(qs)) selectedSize.value = qs;

    const byId = qv
      ? (variants.value || []).find((v) => String(v.id) === qv)
      : null;
    if (!byId) return;

    selectedVariantId.value = qv;
    // Колір і розмір беремо з САМОГО лота, а не лише з ?color/?size.
    // Картка каталогу шле всі три параметри, але сторінка вміє писати в
    // URL один `vid` (selectLot → replaceState), і такий лінк цілком
    // ходить по руках. Без цього `matchingVariants` фільтрувався б за
    // дефолтним кольором, потрібного лота там не було б — і SSR тихо
    // показав би перший-ліпший варіант.
    const c = byId.color?.value ?? byId.color ?? "";
    const sz = byId.size ?? "";
    if (c) selectedColor.value = String(c);
    if (sz) selectedSize.value = String(sz);
  }

  watch(
    () => [route.query.color, route.query.size, route.query.vid],
    applyQueryOptions,
  );

  /* ---- Matching variants ---- */
  const matchingVariants = computed(() => {
    const c = selectedColor.value;
    const s = selectedSize.value;
    const list = (variants.value || []).filter((v) => {
      const vc = v?.color?.value || v?.color || "";
      const vs = String(v?.size || "");
      const colorOk = !c || vc === c;
      const sizeOk = !s || vs === String(s || "");
      return colorOk && sizeOk;
    });

    const score = (v) => {
      const ex = isExclusiveVariant(v) ? 10 : 0;
      const stock = (readVariantQty(v) ?? 0) > 0 ? 0 : 100;
      return ex + stock;
    };
    return list.sort((a, b) => score(a) - score(b));
  });

  const currentVariant = computed(() => {
    if (selectedVariantId.value) {
      const found = (matchingVariants.value || []).find(
        (v) => String(v.id) === String(selectedVariantId.value),
      );
      if (found) return found;
    }
    return matchingVariants.value?.[0] || null;
  });

  /* ---- Exclusive ---- */
  const isExclusive = computed(() => isExclusiveVariant(currentVariant.value));
  const exclusiveBadgeText = computed(() =>
    isExclusive.value ? exclusiveTextFromVariant(currentVariant.value) : "",
  );

  /* ---- Lot selector ---- */
  const lotOptions = computed(() => {
    const list = matchingVariants.value || [];
    if (!list.length) return [];
    const hasMultiple = list.length > 1;
    const hasExclusive = list.some((v) => isExclusiveVariant(v));
    if (!hasMultiple && !hasExclusive) return [];

    return list.map((v) => {
      const ex = isExclusiveVariant(v);
      const stock = (readVariantQty(v) ?? 0) > 0;
      const labelBase = ex ? exclusiveTextFromVariant(v) : "Звичайний";
      const suffix = stock ? "" : " (немає)";
      return {
        id: String(v.id),
        label: `${labelBase}${suffix}`,
        title: ex
          ? `${labelBase}${stock ? "" : " — немає в наявності"}`
          : `Звичайний лот${stock ? "" : " — немає в наявності"}`,
      };
    });
  });

  const lotHint = computed(() => {
    if (!lotOptions.value.length) return "";
    if (isExclusive.value) return "Це ексклюзивний лот для обраних параметрів.";
    if (lotOptions.value.some((o) => o.label.startsWith("Ексклюзив")))
      return "Для цих параметрів доступні звичайний та ексклюзивний лоти.";
    return "";
  });

  function selectLot(variantId) {
    selectedVariantId.value = String(variantId || "");
    if (import.meta.client) {
      const q = { ...route.query };
      if (selectedVariantId.value) q.vid = selectedVariantId.value;
      else delete q.vid;
      try {
        const u = new URL(window.location.href);
        Object.keys(q).forEach((k) => {
          const v = q[k];
          if (v === undefined) u.searchParams.delete(k);
          else u.searchParams.set(k, String(v));
        });
        window.history.replaceState({}, "", u.toString());
      } catch {
        /* ignore */
      }
    }
  }

  /* ---- Auto-reset on option change ---- */
  watch(
    () => [selectedColor.value, selectedSize.value],
    () => {
      const cur = currentVariant.value;
      if (!cur?.id) {
        selectedVariantId.value = matchingVariants.value?.[0]?.id
          ? String(matchingVariants.value[0].id)
          : "";
        return;
      }
      selectedVariantId.value = String(cur.id);
    },
  );

  /* ---- Derived state ---- */
  const priceToShow = computed(() =>
    currentVariant.value
      ? Number(currentVariant.value.price || 0)
      : Number(product.value?.price || 0),
  );

  const inStock = computed(
    () => (readVariantQty(currentVariant.value ?? product.value) ?? 0) > 0,
  );

  // inStock тут навмисно строгіший за variantDisplay.isItemInStock
  // (тільки числовий залишок, без bool-прапорців) — семантика сторінки
  // товару. `readVariantQty`, а не `v.stock`: у сабколекції поле
  // зветься `stock`, а в `variantsLite` — `stockQty`, і на голому
  // `v.stock` увесь фікс `OutOfStock` мовчки не спрацював би.
  const isLonelyBeauty = computed(
    () => inStock.value === true && readVariantQty(currentVariant.value) === 1,
  );

  const mustChooseOptions = computed(
    () => product.value.colors?.length || product.value.sizes?.length,
  );

  /* ---- SKU ---- */
  const variantSku = computed(() =>
    currentVariant.value?.sku
      ? currentVariant.value.sku
      : product.value?.sku
        ? [
            normSkuPart(product.value.sku),
            normSkuPart(selectedColor.value),
            normSkuPart(selectedSize.value),
          ]
            .filter(Boolean)
            .join("-")
        : "",
  );

  const skuMasked = computed(() => {
    const raw =
      currentVariant.value?.sku || product.value?.sku || variantSku.value || "";
    return maskSkuPairs(raw, 4);
  });

  return {
    selectedColor,
    selectedSize,
    selectedVariantId,
    variants,
    matchingVariants,
    currentVariant,
    isExclusive,
    exclusiveBadgeText,
    lotOptions,
    lotHint,
    selectLot,
    priceToShow,
    inStock,
    isLonelyBeauty,
    mustChooseOptions,
    variantSku,
    skuMasked,
    getColorValue,
  };
}
